import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  MultiStepFormProvider,
  SubmitHandlerWithDirtyFields,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { CreateTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/create-tariff.model';
import { useMemo } from 'react';
import { Path, SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TariffCreateFormStep1Component } from '@app/feature/tariff/tariff-create/tariff-create-form-step-1/tariff-create-form-step-1.component';
import { TariffCreateFormStep2AlternateComponent } from '@app/feature/tariff/tariff-create/tariff-create-form-step-2-alternate/tariff-create-form-step-2-alternate.component';
import { TariffCreateFormStep2Component } from '@app/feature/tariff/tariff-create/tariff-create-form-step-2/tariff-create-form-step-2.component';
import { TariffCreateFormStep3Component } from '@app/feature/tariff/tariff-create/tariff-create-form-step-3/tariff-create-form-step-3.component';
import {
  useCreateTariffContext,
  useTariffUIContext,
} from '@app/feature/tariff/tariff.context';

export interface TariffCreateFormControllerProps {
  onError: SubmitErrorHandler<CreateTariffModel>;
  onSubmit: SubmitHandlerWithDirtyFields<CreateTariffModel>;
  onCancel: () => void;
  state: MultiStepFormState;
}

export const TariffCreateFormController = (
  props: TariffCreateFormControllerProps,
): React.JSX.Element => {
  const { onError, onSubmit, onCancel, state } = props;
  const { t } = useTranslation();
  const data = useCreateTariffContext();
  const { isPriceRange } = useTariffUIContext();

  const items = useMemo(() => {
    if (isPriceRange) {
      return [
        {
          title: t('$.tariff-create.step-1.title'),
          iconName: 'info-circle',
          fields: ['name', 'currency', 'elements'] as Path<CreateTariffModel>[],
          component: <TariffCreateFormStep1Component />,
        },
        {
          title: t('$.tariff-create.step-2.title'),
          iconName: 'info-circle',
          fields: ['elements'] as Path<CreateTariffModel>[],
          component: <TariffCreateFormStep2Component />,
        },
        {
          title: t('$.tariff-create.step-3.title'),
          iconName: 'info-circle',
          fields: ['elements'] as Path<CreateTariffModel>[],
          component: <TariffCreateFormStep3Component />,
        },
      ];
    }
    return [
      {
        title: t('$.tariff-create.step-1.title'),
        iconName: 'info-circle',
        fields: ['name', 'currency', 'elements'] as Path<CreateTariffModel>[],
        component: <TariffCreateFormStep1Component />,
      },
      {
        title: t('$.tariff-create.step-2-alternate.title'),
        iconName: 'price',
        fields: ['elements'] as Path<CreateTariffModel>[],
        component: <TariffCreateFormStep2AlternateComponent />,
      },
    ];
  }, [t, isPriceRange]);

  return (
    <MultiStepFormProvider<CreateTariffModel>
      useFormProps={{
        mode: 'all',
        defaultValues: data,
      }}
      submitErrorHandler={onError}
      submitHandler={onSubmit}
      onCancel={onCancel}
      state={state}
      items={items}
    />
  );
};
